import { observer } from "mobx-react"
import { type KeyboardEvent, type ReactNode, useEffect, useState } from "react"
import { Resizable } from "re-resizable"
import { dockedPaneWidthBounds, dockedPaneWidthStore } from "../../store/managers/dockedPaneWidthStore"

const KEYBOARD_RESIZE_STEP_PX = 24
const KEYBOARD_RESIZE_LARGE_STEP_PX = 64

interface DockedPaneResizableProps {
    children?: ReactNode
    enabled?: boolean
}

/**
 * Shared right-edge pane boundary. re-resizable owns mouse/touch capture and
 * geometry; the app adapter only synchronizes its controlled width with the
 * shell reservation and the persisted preference.
 */
export const DockedPaneResizable = observer(function DockedPaneResizable({ children, enabled = true }: DockedPaneResizableProps): JSX.Element {
    const [viewportWidth, setViewportWidth] = useState(() => (typeof window === "undefined" ? 1280 : window.innerWidth))
    const bounds = dockedPaneWidthBounds(viewportWidth)
    const currentWidth = dockedPaneWidthStore.currentWidth(viewportWidth)

    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth)
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const step = event.shiftKey ? KEYBOARD_RESIZE_LARGE_STEP_PX : KEYBOARD_RESIZE_STEP_PX
        if (event.key === "ArrowLeft") {
            event.preventDefault()
            dockedPaneWidthStore.setWidth(currentWidth + step, viewportWidth)
        } else if (event.key === "ArrowRight") {
            event.preventDefault()
            dockedPaneWidthStore.setWidth(currentWidth - step, viewportWidth)
        } else if (event.key === "Home") {
            event.preventDefault()
            dockedPaneWidthStore.setWidth(bounds.min, viewportWidth)
        } else if (event.key === "End") {
            event.preventDefault()
            dockedPaneWidthStore.setWidth(bounds.max, viewportWidth)
        }
    }

    const syncPreviewWidth = (element: HTMLElement) => {
        dockedPaneWidthStore.previewWidth(element.getBoundingClientRect().width, window.innerWidth)
    }

    return (
        <Resizable
            className="flex min-h-0 flex-col"
            style={enabled ? { position: "absolute", insetBlock: 0, right: 0 } : { position: "relative" }}
            size={enabled ? { width: currentWidth, height: "100%" } : { width: "100%", height: "100%" }}
            minWidth={enabled ? bounds.min : undefined}
            maxWidth={enabled ? bounds.max : undefined}
            enable={enabled ? { left: true } : false}
            bounds={enabled ? "window" : undefined}
            boundsByDirection={enabled}
            onResizeStart={() => dockedPaneWidthStore.beginResize()}
            onResize={(_event, _direction, element) => syncPreviewWidth(element)}
            onResizeStop={(_event, _direction, element) => {
                syncPreviewWidth(element)
                dockedPaneWidthStore.finishResize()
            }}
            handleComponent={
                enabled
                    ? {
                          left: (
                              <div
                                  role="separator"
                                  aria-label="Resize docked pane"
                                  aria-orientation="vertical"
                                  aria-valuemin={bounds.min}
                                  aria-valuemax={bounds.max}
                                  aria-valuenow={currentWidth}
                                  tabIndex={0}
                                  className="group relative h-full w-full cursor-col-resize touch-none outline-none"
                                  title="Drag to resize · Double-click to reset"
                                  onKeyDown={handleKeyDown}
                                  onDoubleClick={() => dockedPaneWidthStore.reset()}
                                  data-docked-pane-resize-handle="true"
                              >
                                  <span className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border transition-[width,background-color] group-hover:w-0.5 group-hover:bg-primary group-focus-visible:w-0.5 group-focus-visible:bg-primary" />
                              </div>
                          ),
                      }
                    : undefined
            }
            data-docked-pane-resizable={enabled || undefined}
        >
            {children}
        </Resizable>
    )
})
